import React, { useState } from 'react';
import propTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { useMapStateStore } from '../../stores';

import './FloorPlanInventoryList.scss';

// Collapse the list to this many residences until the user expands it.
const INITIAL_VISIBLE_COUNT = 3;

const FloorPlanInventoryList = ({
  uuid,
  availableResidences,
  floorPlanCategory,
  isOpen,
}) => {
  const history = useHistory();
  const [isExpanded, setIsExpanded] = useState(false);
  const setSvgFocusId = useMapStateStore((state) => state.setSvgFocusId);
  const setHasPendingResidenceFocus = useMapStateStore(
    (state) => state.setHasPendingResidenceFocus,
  );

  const availableCount = availableResidences?.length || 0;

  if (availableCount === 0) {
    return null;
  }

  // Floor plan category labels are stored plural (e.g. "Cottages"), so use as-is
  // for the count phrase and strip a trailing "s" when there is only one.
  const availableTypeLabel = floorPlanCategory || 'Residences';
  const availableTypeSingular = availableTypeLabel.replace(/s$/i, '');

  const visibleResidences = isExpanded
    ? availableResidences
    : availableResidences.slice(0, INITIAL_VISIBLE_COUNT);
  const hiddenCount = availableCount - INITIAL_VISIBLE_COUNT;
  const hasHiddenResidences = hiddenCount > 0;
  const hiddenTypeLabel = hiddenCount === 1 ? availableTypeSingular : availableTypeLabel;

  // Send the user to the Interactive Map page focused on this residence's unit.
  const handleViewOnMap = (svgId) => {
    if (!svgId) {
      return;
    }
    setSvgFocusId(svgId);
    setHasPendingResidenceFocus(true);
    history.push('/map');
  };

  return (
    <div
      id={`available-residences-inventory:${uuid}`}
      className={classNames('floorplanInfo__inventory', {
        'floorplanInfo__inventory--hidden': !isOpen,
      })}
    >
      <span className="floorplanInfo__inventory__arrow" aria-hidden="true" />

      <div className="floorplanInfo__inventory__inner">

        { visibleResidences.map((residence) => (
          <div
            key={residence.uuid}
            className="floorplanInfo__residence"
            data-testid={`available-residence:${residence.uuid}`}
          >
            <div className="floorplanInfo__residence__name">{residence.name}</div>

            <div className="floorplanInfo__residence__details">
              { residence.location && (
              <div className="floorplanInfo__residence__detail">
                {residence.location}
                {residence.floor && ','}
              </div>
              )}
              { residence.floor && (
              <div className="floorplanInfo__residence__detail">{residence.floor}</div>
              )}
            </div>

            { residence.svgId && (
              <button
                type="button"
                className="floorplanInfo__residence__mapBtn"
                onClick={() => handleViewOnMap(residence.svgId)}
                data-testid={`view-on-map:${residence.uuid}`}
              >
                View on Map
              </button>
            )}
          </div>
        ))}

        {hasHiddenResidences && (
          <button
            type="button"
            className="floorplanInfo__residence floorplanInfo__showMoreBtn"
            onClick={() => setIsExpanded((expanded) => !expanded)}
            data-testid={`available-residences-show-more:${uuid}`}
          >
            {isExpanded ? 'Show Less' : `${hiddenCount} More ${hiddenTypeLabel}`}
          </button>
        )}
      </div>
    </div>
  );
};

FloorPlanInventoryList.propTypes = {
  uuid: propTypes.string,
  availableResidences: propTypes.arrayOf(
    propTypes.shape({
      uuid: propTypes.string,
      name: propTypes.string,
      location: propTypes.string,
      floor: propTypes.string,
      svgId: propTypes.string,
    }),
  ),
  floorPlanCategory: propTypes.string,
  isOpen: propTypes.bool,
};

FloorPlanInventoryList.defaultProps = {
  uuid: null,
  availableResidences: [],
  floorPlanCategory: null,
  isOpen: false,
};

export default FloorPlanInventoryList;
